import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const Sparkline = ({ points = [], tone = 'positive' }) => {
  if (!points.length) return null;
  const width = 42;
  const height = 20;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;

  const path = points
    .map((point, index) => {
      const x = (index / Math.max(points.length - 1, 1)) * width;
      const y = height - (((point - min) / range) * (height - 2)) - 1;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg className={`executive-overview-sparkline ${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={path} fill="none" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
};

const formatKpiValue = (dataset) => {
  if (dataset.unit === '%') return `${Number(dataset.value).toFixed(1)}%`;
  if (dataset.unit === 'days' || dataset.unit === 'أيام') return `${Number(dataset.value).toFixed(1)} ${dataset.unit}`;
  return `${Number(dataset.value).toFixed(1)}${dataset.unit ? ` ${dataset.unit}` : ''}`;
};

const getTrendTone = (trendValue) => {
  if (typeof trendValue !== 'number' || Number.isNaN(trendValue) || trendValue === 0) {
    return 'warning';
  }
  return trendValue > 0 ? 'success' : 'danger';
};

const KpiVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');
  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);
  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const sizeClass = model.size || 'medium';
  if (!dataset) return null;

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD) {
    return (
      <div className={`analytics-visual analytics-visual-offer-signal ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-offer-signal-kpis">
          {(dataset.highlights || []).map((item) => (
            <div key={item.id} className="analytics-visual-offer-signal-kpi">
              <span className="executive-overview-kpi-label analytics-visual-offer-signal-label">{uiText(item.label)}</span>
              <strong className="executive-overview-kpi-value analytics-visual-offer-signal-value">
                <span className="executive-overview-kpi-value-main">
                  {item.unit === '%'
                    ? `${Number(item.value).toFixed(1)}%`
                    : Number(item.value).toLocaleString()}
                </span>
                {item.unit && item.unit !== '%' ? (
                  <span className="executive-overview-kpi-value-unit">{item.unit}</span>
                ) : null}
              </strong>
              <div className="executive-overview-kpi-footer analytics-visual-offer-signal-footer">
                <span className={`executive-overview-kpi-sub ${item.tone || 'positive'}`}>{item.sublabel}</span>
                <Sparkline points={item.trend} tone={item.tone || 'positive'} />
              </div>
            </div>
          ))}
        </div>
        <div className="analytics-visual-offer-signal-reasons">
          <div className="analytics-visual-offer-signal-reasons-title">{dataset.reasonsTitle}</div>
          <div className="analytics-visual-offer-signal-reasons-list">
            {(dataset.reasons || []).map((reason) => (
              <div key={reason.id} className="analytics-visual-offer-signal-reason">
                <span className="analytics-visual-offer-signal-reason-label">{uiText(reason.label)}</span>
                <span className="analytics-visual-offer-signal-dash" aria-hidden="true" />
                <strong className="analytics-visual-offer-signal-reason-count">{reason.count}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.GAUGE) {
    const minimum = dataset.minimum ?? 0;
    const maximum = dataset.maximum ?? 100;
    const percent = Math.max(0, Math.min(100, ((dataset.value - minimum) / Math.max(maximum - minimum, 1)) * 100));
    const dashOffset = 251.2 - ((percent / 100) * 251.2);
    return (
      <div className={`analytics-visual analytics-visual-kpi-gauge ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-radial">
          <svg viewBox="0 0 96 96" className="analytics-visual-radial-svg" role="img" aria-label={dataset.title}>
            <circle cx="48" cy="48" r="40" className="analytics-visual-radial-base" />
            <circle
              cx="48"
              cy="48"
              r="40"
              className="analytics-visual-radial-progress accent"
              strokeDasharray="251.2"
              strokeDashoffset={dashOffset}
            />
          </svg>
          <strong className="analytics-visual-kpi-number">{formatKpiValue(dataset)}</strong>
          {dataset.trendLabel ? <span className="analytics-visual-sub">{dataset.trendLabel}</span> : null}
        </div>
      </div>
    );
  }

  return (
      <div className={`analytics-visual analytics-visual-kpi ${sizeClass}`}>
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
      ) : null}
      <strong className="analytics-visual-kpi-number">{formatKpiValue(dataset)}</strong>
      {dataset.trendValue || dataset.trendLabel ? (
        <span className={`analytics-visual-sub ${getTrendTone(dataset.trendValue)}`}>
          {dataset.trendValue ? `${dataset.trendValue > 0 ? '+' : ''}${dataset.trendValue.toFixed(1)} ` : ''}
          {dataset.trendLabel}
        </span>
      ) : null}
      {dataset.trendSeries ? (
        <div className="analytics-visual-kpi-sparkline">
          <svg viewBox="0 0 240 70" className="analytics-visual-trend-svg" role="img" aria-label={uiText("KPI trend sparkline")}>
            <polyline
              points={(dataset.trendSeries.series[0]?.values || [])
                .map((value, index, values) => {
                  const max = Math.max(...values, 1);
                  const x = 10 + (index / Math.max(values.length - 1, 1)) * 220;
                  const y = 56 - ((value / max) * 42);
                  return `${x},${y}`;
                })
                .join(' ')}
              className="analytics-visual-line accent"
            />
          </svg>
        </div>
      ) : null}
    </div>
  );
};

export default KpiVisual;
